import {
  type PlayerListBase,
  type RpmPickList,
  type RpmPicks,
  RpmType,
} from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { createUrl } from '@on3/ui-lib/utils/url';
import { RpmConfidenceChange } from '@public/components/Rpm/ConfidenceChange';
import { RpmNewLeader } from '@public/components/Rpm/NewLeader';
import { RpmPercentChange } from '@public/components/Rpm/PercentChange';
import { RpmPick } from '@public/components/Rpm/Pick';
import { StarRatings } from '@public/components/UI/StarRatings/StarRatings';
import { format } from 'date-fns';

import styles from './PlayerPrediction.module.scss';

const getPickType = (type: string) => {
  switch (type) {
    case RpmType.ConfidenceChange:
      return 'Change in confidence level';
    case RpmType.NewLeader:
      return 'New leader';
    case RpmType.PercentChange:
      return 'Change in rpm';
    default:
      return null;
  }
};

const getRpmType = (pick: RpmPicks): JSX.Element => {
  switch (pick.type) {
    case RpmType.ConfidenceChange:
      return <RpmConfidenceChange pick={pick} />;
    case RpmType.NewLeader:
      return <RpmNewLeader pick={pick} />;
    case RpmType.PercentChange:
      return <RpmPercentChange pick={pick} />;
    default:
      return <RpmPick pick={pick} />;
  }
};

export const PlayerPrediction = ({ pick, player }: RpmPickList) => {
  pick = pick ?? ({} as RpmPicks);
  player = player ?? ({} as PlayerListBase);
  const pickType = getPickType(pick.type);

  return (
    <section className={styles.block}>
      {pickType && <p className={styles.pickType}>{pickType}</p>}
      <div className={styles.playerContainer}>
        <figure>
          <Avatar
            altText={`${player.name} avatar`}
            className={styles.playerAvatar}
            domain={player.defaultAsset?.domain}
            fit="cover"
            height={50}
            source={player.defaultAsset?.source}
            width={50}
          />
        </figure>
        <div className={styles.info}>
          <Link
            className={styles.name}
            href={`/rivals/${player.slug}-${player.key}/`}
          >
            {player.name}
          </Link>
          <div className={styles.vitals}>
            {player.positionAbbreviation && (
              <span>{player.positionAbbreviation}</span>
            )}
            {player.height && <span>{player.height}</span>}
            {player.weight && <span>{player.weight}</span>}
          </div>
          <div className={styles.rating}>
            <Link
              href={createUrl([
                'rivals',
                'rankings',
                'industry-player',
                slugify(player.sport?.name || ''),
                player.classYear,
              ])}
            >
              <StarRatings stars={player.rating?.stars || 0} />
              <span className="sr-only">View Industry Comparison</span>
            </Link>
            {player.rating && <span>{player.rating.rating?.toFixed(2)}</span>}
          </div>
        </div>
        {!!player.nilValue && (
          <dl className={styles.nil}>
            <dt>NIL Value:</dt>
            <dd>{formatCurrency(player.nilValue ?? 0)}</dd>
          </dl>
        )}
      </div>
      <div className={styles.predictionContainer}>{getRpmType(pick)}</div>
      {pick.expert && (
        <div className={styles.expertContainer}>
          <figure className={styles.avatarContainer}>
            <Avatar
              altText={`${pick.expert.name} avatar`}
              className={styles.expertAvatar}
              domain={pick.expert.profilePictureResponse?.domain}
              fit="cover"
              height={30}
              source={pick.expert.profilePictureResponse?.source}
              width={30}
            />
          </figure>
          <div className={styles.expertInfo}>
            <Link
              className={styles.expertName}
              href={createUrl(['user', pick.expert.niceName, 'predictions'])}
            >
              {pick.expert.name}
            </Link>
            {pick.expert.jobTitle && <span>{pick.expert.jobTitle}</span>}
          </div>
        </div>
      )}
      <div className={styles.metaContainer}>
        {pick.dateAdded && (
          <p className={styles.date}>
            {format(new Date(pick.dateAdded), 'L/d/yy p')}
          </p>
        )}
        {pick.articleLink && (
          <Link className={styles.articleLink} href={pick.articleLink}>
            Intel
          </Link>
        )}
      </div>
    </section>
  );
};
